/**
 * Escala de cores da marca, derivada de uma única cor base.
 *
 * O painel inteiro usa os tokens `marca`, `marca-escura`, `marca-clara`,
 * `marca-suave` e `marca-tenue` em vez de cores fixas. Assim, trocar a cor do
 * sistema na tela de identidade repinta tudo — barra, menu lateral, títulos,
 * botões — sem depender de alteração em cada tela.
 *
 * Os nomes evitam "primary" de propósito: `primary` já é o verde-azulado do
 * tema padrão do Vuetify neste projeto, e sobrescrevê-lo mudaria também
 * componentes que hoje dependem daquele tom.
 */

/** Cor institucional usada quando a associação não definiu uma própria. */
export const COR_MARCA_PADRAO = "#0D47A1";

function paraRgb(hex) {
  const limpo = String(hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(limpo)) return null;
  return {
    r: parseInt(limpo.slice(0, 2), 16),
    g: parseInt(limpo.slice(2, 4), 16),
    b: parseInt(limpo.slice(4, 6), 16),
  };
}

function paraHex({ r, g, b }) {
  const parte = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${parte(r)}${parte(g)}${parte(b)}`.toUpperCase();
}

/** Mistura a cor com branco (fator > 0) ou preto (fator < 0). */
function misturar(rgb, fator) {
  const alvo = fator > 0 ? 255 : 0;
  const peso = Math.abs(fator);
  return {
    r: rgb.r + (alvo - rgb.r) * peso,
    g: rgb.g + (alvo - rgb.g) * peso,
    b: rgb.b + (alvo - rgb.b) * peso,
  };
}

/**
 * Luminância relativa, para decidir se o texto sobre a cor deve ser branco
 * ou preto. Sem isso, uma cor clara escolhida pela instituição deixaria a
 * barra superior com texto branco ilegível.
 */
export function contrasteSobre(hex) {
  const rgb = paraRgb(hex);
  if (!rgb) return "#FFFFFF";
  const canal = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const l = 0.2126 * canal(rgb.r) + 0.7152 * canal(rgb.g) + 0.0722 * canal(rgb.b);
  return l > 0.45 ? "#1A1A1A" : "#FFFFFF";
}

/**
 * Devolve o conjunto de tokens da marca a partir da cor base. Cor inválida
 * cai no padrão, para o painel nunca ficar sem cor.
 */
export function escalaMarca(corBase) {
  const rgb = paraRgb(corBase) || paraRgb(COR_MARCA_PADRAO);
  const base = paraHex(rgb);
  return {
    marca: base,
    "marca-escura": paraHex(misturar(rgb, -0.35)),
    "marca-clara": paraHex(misturar(rgb, 0.25)),
    "marca-suave": paraHex(misturar(rgb, 0.86)),
    "marca-tenue": paraHex(misturar(rgb, 0.94)),
    "sobre-marca": contrasteSobre(base),
  };
}

/**
 * Aplica a escala nos dois temas do Vuetify. O tema escuro recebe a versão
 * clareada da cor: a cor institucional costuma ser escura demais para servir
 * de fundo sobre um tema já escuro.
 */
export function aplicarCorNoTema(vuetifyTheme, corBase) {
  const claro = escalaMarca(corBase);
  const escuro = escalaMarca(claro["marca-clara"]);

  // `themes` é um ref quando vem de useTheme(), mas chega desembrulhado em
  // this.$vuetify.theme, porque o $vuetify é um reactive() e o Vue desfaz os
  // refs. Aceitar as duas formas evita quebrar conforme o ponto de chamada.
  const temas = vuetifyTheme?.themes?.value ?? vuetifyTheme?.themes;
  if (!temas?.light?.colors || !temas?.dark?.colors) {
    return;
  }

  Object.assign(temas.light.colors, claro);
  Object.assign(temas.dark.colors, {
    ...escuro,
    "marca-suave": escalaMarca(corBase)["marca-escura"],
    "marca-tenue": escalaMarca(corBase)["marca-escura"],
  });
}
